'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { clientEnv } from '../../env.client';

// =============================================================================
// Client-side reCAPTCHA v3 loader + token minting.
//
// Reference-counted: several components on one page can mount this hook and
// the api.js tag is injected once, then removed only when the last consumer
// unmounts. That matters on inventory item pages, where the "Inquire" gate and
// the inquiry form itself both need grecaptcha alive at the same time.
//
// Loading is opt-in per consumer via `enabled`. The footer signup sits on every
// page, so it defers loading until someone actually touches the field —
// otherwise every page on the site would pull Google's script and float the
// reCAPTCHA badge over the layout.
// =============================================================================

const SCRIPT_ID = 'recaptcha-script';

/** Consumers currently requesting the script. It outlives any single one. */
let consumers = 0;

/** How long execute() will wait for a script that is still loading. */
const READY_TIMEOUT_MS = 5_000;

type Grecaptcha = {
    ready: (cb: () => void) => void;
    execute: (siteKey: string, opts: { action: string }) => Promise<string>;
};

function getGrecaptcha(): Grecaptcha | undefined {
    return (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
}

function teardown(): void {
    document.getElementById(SCRIPT_ID)?.remove();
    document.querySelectorAll('.grecaptcha-badge').forEach(el => el.remove());
    document.querySelectorAll('iframe[src*="recaptcha"]').forEach(el => el.remove());
    delete (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
}

export type UseRecaptchaOptions = {
    /**
     * Whether this consumer wants the script loaded. Flip it to true on first
     * interaction to defer the cost until the form is actually being used.
     */
    enabled?: boolean;
};

export type UseRecaptcha = {
    /** True once api.js has loaded and grecaptcha is callable. */
    ready: boolean;
    /** True when no site key is configured — callers should degrade, not block. */
    disabled: boolean;
    /**
     * Mint a token for `action`. Waits briefly for a script that is still
     * loading. Resolves to null when reCAPTCHA is genuinely unavailable (no
     * key, blocked, or execute failed) so the caller can still submit and let
     * the server's other guards decide.
     */
    execute: (action: string) => Promise<string | null>;
};

export function useRecaptcha({ enabled = true }: UseRecaptchaOptions = {}): UseRecaptcha {
    const siteKey = clientEnv.RECAPTCHA_SITE_KEY;
    const [ready, setReady] = useState(false);
    const mounted = useRef(true);

    useEffect(() => {
        if (!siteKey || !enabled) return;

        mounted.current = true;
        consumers += 1;

        const markReady = () => {
            if (mounted.current) setReady(true);
        };

        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (existing) {
            // Another consumer already injected it; it may still be loading.
            if (getGrecaptcha()) markReady();
            else existing.addEventListener('load', markReady);
        } else {
            const script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
            script.async = true;
            script.onload = markReady;
            script.onerror = () => {
                // Blocked by an extension or unreachable. `ready` stays false;
                // execute() resolves null and the server decides.
                console.warn('[recaptcha] api.js failed to load');
            };
            document.head.appendChild(script);
        }

        return () => {
            mounted.current = false;
            existing?.removeEventListener('load', markReady);
            consumers = Math.max(0, consumers - 1);
            if (consumers === 0) teardown();
        };
    }, [siteKey, enabled]);

    /**
     * Resolve once grecaptcha exists, or after the timeout. Covers the case
     * where a lazily-enabled script is still in flight when the user submits.
     */
    const waitForGrecaptcha = useCallback(async (): Promise<Grecaptcha | undefined> => {
        const immediate = getGrecaptcha();
        if (immediate) return immediate;

        const deadline = Date.now() + READY_TIMEOUT_MS;
        while (Date.now() < deadline) {
            await new Promise(resolve => setTimeout(resolve, 100));
            const found = getGrecaptcha();
            if (found) return found;
        }
        return undefined;
    }, []);

    const execute = useCallback(
        async (action: string): Promise<string | null> => {
            if (!siteKey) return null;

            const grecaptcha = await waitForGrecaptcha();
            if (!grecaptcha) {
                console.warn(`[recaptcha] unavailable when minting token for '${action}'`);
                return null;
            }

            try {
                return await new Promise<string>((resolve, reject) => {
                    grecaptcha.ready(() => {
                        grecaptcha.execute(siteKey, { action }).then(resolve, reject);
                    });
                });
            } catch (error) {
                console.warn(`[recaptcha] execute('${action}') failed:`, error);
                return null;
            }
        },
        [siteKey, waitForGrecaptcha]
    );

    return { ready, disabled: !siteKey, execute };
}
